import { getElementPositionRelativeToViewport } from '@/globals/typescript/utils/utils'
import { defineStore } from 'pinia'

export type GlobalMenuWidth = 'dynamic' | 'md'
export type GlobalMenuPlacement = 'auto' | 'right-of-trigger'

export type GlobalMenuEntry = {
  id: string
  name: string
  props?: Record<string, unknown>
  triggerElement: HTMLElement
  position: {
    top: number
    left: number
    right: number
    showAbove: boolean
  }
  width?: GlobalMenuWidth
}

export const useGlobalMenuStore = defineStore('globalMenu', {
  state: () => ({
    currentMenu: null as GlobalMenuEntry | null,
  }),

  actions: {
    open({
      name,
      triggerElement,
      props = {},
      width = 'md',
      placement = 'auto',
    }: {
      name: string
      triggerElement: HTMLElement
      props?: Record<string, unknown>
      width?: 'dynamic' | 'md'
      placement?: GlobalMenuPlacement
    }) {
      if (this.currentMenu) {
        // Close current menu if same trigger + same menu is called (toggle)
        if (
          this.currentMenu.name === name &&
          this.currentMenu.triggerElement === triggerElement
        ) {
          this.currentMenu = null
          return
        }
      }

      // Create a unique ID for this menu instance
      const id = `${name}:${Date.now()}:${Math.random().toString(16).slice(2)}`

      // Calulate the position of the menu based on the trigger element
      const { horizontal, vertical } =
        getElementPositionRelativeToViewport(triggerElement)

      const position = {
        top: 0,
        left: 0,
        right: 0,
        showAbove: false,
      }

      const { left, right, top, bottom } =
        triggerElement.getBoundingClientRect()
      if (placement === 'right-of-trigger') {
        position.left = right
      } else if (horizontal === 'right') {
        position.right = window.innerWidth - right
      } else {
        position.left = left
      }
      if (vertical === 'top') {
        position.top = bottom + 3
      }
      if (vertical === 'bottom') {
        position.top = top - 3
        position.showAbove = true
      }

      // Open the new menu
      this.currentMenu = { id, name, triggerElement, props, position, width }
    },

    close() {
      this.currentMenu = null
    },
  },
})

